import { DestroyRef, inject, Injectable, signal, computed, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingState } from '@app/shared/interfaces';
import { DynamicPricingOptionsResponse } from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { BehaviorSubject, filter, map, of, switchMap, take } from 'rxjs';

interface DynamicPricingServiceState {
  $config: WritableSignal<DynamicPricingOptionsResponse | null>;
  $configLoading: WritableSignal<LoadingState>;
  $propertyId: WritableSignal<number | null>;
}

type StateKey = keyof DynamicPricingServiceState;

@Injectable({
  providedIn: 'root',
})
export class DynamicPricingService {
  // Dependencies
  private pusherService: PusherService = inject(PusherService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private posthogService: PosthogService = inject(PosthogService);
  private propertiesService: PropertiesService = inject(PropertiesService);

  // State signals
  private state: DynamicPricingServiceState = {
    $config: signal<DynamicPricingOptionsResponse | null>(null),
    $configLoading: signal(LoadingState.NotSent),
    $propertyId: signal<number | null>(null),
  };

  // Selectors
  public $config = computed(() => this.state.$config());
  public $dynamicPricingOn = computed(() => this.state.$config()?.outbound_dynamic_pricing_enabled);
  public $configLoading = computed(() => this.state.$configLoading());
  public $propertyId = computed(() => this.state.$propertyId());

  // Observables for state
  public dynamicPricingEnabled$ = this.posthogService.dynamicPricingEnabled$;
  private enableOutboundDynamicPricingLoading$ = new BehaviorSubject<LoadingState>(LoadingState.NotSent);
  public dynamicPricingPusherUpdates$ = new BehaviorSubject<boolean>(false);
  public enableDynamicPricingProgress$ = this.enableOutboundDynamicPricingLoading$.asObservable().pipe(
    switchMap((loading) => {
      if (loading !== LoadingState.Success) {
        return of(1);
      }

      return this.dynamicPricingPusherUpdates$.pipe(
        filter((updated) => updated),
        take(1),
        map(() => 2)
      );
    })
  );
  /**
   * Initialize the dynamic pricing service for a property
   * @param propertyId - The ID of the property to initialize the service for
   */
  public init(propertyId: number) {
    this.resetState();
    this.updateState('$propertyId', propertyId);
    this.loadDynamicPricingConfig();
    this.pusherService
      .getDynamicPricingRecommendationsUpdate(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadDynamicPricingConfig();
        this.dynamicPricingPusherUpdates$.next(true);
      });
  }

  public resetState() {
    this.updateState('$propertyId', null);
    this.updateState('$configLoading', LoadingState.NotSent);
    this.updateState('$config', null);
  }

  public loadDynamicPricingConfig() {
    this.updateState('$configLoading', LoadingState.Pending);
    this.propertiesService.getDynamicPricingOptions(this.$propertyId()).subscribe((data) => {
      this.updateState('$config', data as DynamicPricingOptionsResponse);
      this.updateState('$configLoading', LoadingState.Success);
    });
  }

  // TODO: this just updates the state for now
  public setDynamicPricingConfig(payload: DynamicPricingOptionsResponse) {
    this.updateState('$config', payload);
  }

  public activateOutboundDynamicPricing() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Pending);
  }

  public activateOutboundDynamicPricingSuccess() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Success);
  }

  /**
   * Updates a specific piece of state
   * @param key - The state key to update
   * @param value - The new value
   */
  private updateState(key: StateKey, value: unknown) {
    const signal = this.state[key];
    if (key === '$config' && value !== null) {
      const currentValue = signal() || {};
      // @ts-expect-error - Dynamic typing of value is tricky here
      signal.set({ ...currentValue, ...value });
    } else {
      signal.set(value as never);
    }
  }
}
